import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut, Menu, X } from 'lucide-react';

import { useAuth } from '@/lib/auth/useAuth';
import { useNotifications, useMarkNotificationRead } from '@/hooks/useNotifications';
import { useEnsureTimezone } from '@/hooks/useUserProfile';
import {
  MessagesUnreadProvider,
  useMessagesUnreadCount,
} from '@/lib/messaging/UnreadContext';
import { Wordmark } from '@/components/layout/Wordmark';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  emoji: string;
}

const NAV: NavItem[] = [
  { to: '/app', label: 'Today', emoji: '🏠' },
  { to: '/app/care-recipient', label: 'Care recipient', emoji: '👤' },
  { to: '/app/medications', label: 'Medications', emoji: '💊' },
  { to: '/app/tasks', label: 'Tasks', emoji: '✅' },
  { to: '/app/calendar', label: 'Calendar', emoji: '📅' },
  { to: '/app/feed', label: 'Feed', emoji: '📣' },
  { to: '/app/messages', label: 'Messages', emoji: '💬' },
  { to: '/app/documents', label: 'Vault', emoji: '📁' },
  { to: '/app/family', label: 'Family', emoji: '👨‍👩‍👧' },
  { to: '/app/sage', label: 'Sage', emoji: '🌿' },
];

// Mobile bottom nav — same items as desktop, condensed. Emoji icons are
// intentional, matching the family-facing mockup. Care recipient is
// promoted here too — testers asked for one-tap access during appointments.
const MOBILE_NAV: NavItem[] = [
  { to: '/app', label: 'Today', emoji: '🏠' },
  { to: '/app/care-recipient', label: 'Profile', emoji: '👤' },
  { to: '/app/feed', label: 'Feed', emoji: '💬' },
  { to: '/app/sage', label: 'Sage', emoji: '🌿' },
];

export function AppShell(): JSX.Element {
  const { user, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  // Capture the browser timezone for new accounts so reminder emails are local.
  useEnsureTimezone();

  return (
    <MessagesUnreadProvider>
    <div className="min-h-screen bg-background">
      {/* Mobile drawer */}
      <div
        className={cn(
          'fixed inset-0 z-40 md:hidden',
          mobileOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!mobileOpen}
      >
        <div
          className={cn(
            'absolute inset-0 bg-slate-700/40 transition-opacity',
            mobileOpen ? 'opacity-100' : 'opacity-0',
          )}
          onClick={() => setMobileOpen(false)}
        />
        <aside
          className={cn(
            'absolute left-0 top-0 h-full w-72 bg-surface shadow-md transition-transform',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </aside>
      </div>

      <div className="md:flex">
        <aside className="hidden h-screen w-64 shrink-0 border-r border-border bg-surface md:sticky md:top-0 md:block">
          <SidebarContent />
        </aside>

        <div className="flex min-h-screen flex-1 flex-col">
          <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-surface/80 px-4 py-3 backdrop-blur md:px-8">
            <button
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen((v) => !v)}
              className="rounded-md p-2 text-slate-500 hover:bg-muted md:hidden"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Wordmark size="sm" className="md:hidden" />
            <div className="hidden md:block">
              <p className="text-sm font-semibold text-foreground">{titleFor(location.pathname)}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <div className="flex items-center gap-1">
              <NotificationsBell />
              <button
                type="button"
                onClick={() => {
                  void signOut();
                }}
                aria-label="Sign out"
                className="rounded-md p-2 text-slate-500 hover:bg-muted"
              >
                <LogOut size={18} />
              </button>
            </div>
          </header>

          <main className="flex-1 px-4 pb-28 pt-6 md:px-8 md:pb-10">
            <Outlet />
          </main>

          <nav
            aria-label="Primary"
            className="fixed inset-x-0 bottom-0 z-30 flex justify-around border-t border-border bg-surface/95 pb-3 pt-2 backdrop-blur md:hidden"
          >
            {MOBILE_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/app'}
                className={({ isActive }) =>
                  cn(
                    'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 px-1 text-[11px] font-semibold',
                    isActive ? 'text-brand' : 'text-slate-400',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute -top-2 h-[3px] w-9 rounded-b-md bg-brand" />
                    )}
                    <span
                      className={cn(
                        'text-2xl leading-none transition-transform',
                        isActive ? 'scale-110' : 'scale-100',
                      )}
                      aria-hidden
                    >
                      {item.emoji}
                    </span>
                    <span className="tracking-wide">{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </div>
    </MessagesUnreadProvider>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void } = {}): JSX.Element {
  const unreadMessages = useMessagesUnreadCount();
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-5 py-5">
        <Wordmark />
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/app'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-brand-50 text-brand-800'
                  : 'text-slate-500 hover:bg-muted hover:text-foreground',
              )
            }
          >
            <span className="text-lg leading-none" aria-hidden>
              {item.emoji}
            </span>
            <span className="flex-1">{item.label}</span>
            {item.to === '/app/messages' && unreadMessages > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-semibold text-white">
                {unreadMessages > 9 ? '9+' : unreadMessages}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border px-3 py-3">
        <NavLink
          to="/app/settings"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-50 text-brand-800'
                : 'text-slate-500 hover:bg-muted hover:text-foreground',
            )
          }
        >
          <span className="text-lg leading-none" aria-hidden>
            ⚙️
          </span>
          Settings
        </NavLink>
        <a
          href="mailto:dev@familycareos.com"
          className="mt-1 flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-500 hover:bg-muted hover:text-foreground"
        >
          <span className="text-lg leading-none" aria-hidden>
            💌
          </span>
          Send feedback
        </a>
        <nav
          aria-label="Legal"
          className="mt-3 flex flex-wrap gap-x-3 gap-y-1 border-t border-border px-3 pt-3 text-[11px] text-muted-foreground"
        >
          <NavLink to="/legal/terms" onClick={onNavigate} className="hover:underline">
            Terms
          </NavLink>
          <NavLink to="/legal/privacy" onClick={onNavigate} className="hover:underline">
            Privacy
          </NavLink>
          <NavLink
            to="/legal/notice-of-privacy-practices"
            onClick={onNavigate}
            className="hover:underline"
          >
            HIPAA Notice
          </NavLink>
        </nav>
      </div>
    </div>
  );
}

function NotificationsBell(): JSX.Element {
  const { data: notifications = [] } = useNotifications();
  const markRead = useMarkNotificationRead();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const unread = notifications.filter((n) => !n.read_at);

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={`Notifications${unread.length ? ` (${unread.length} unread)` : ''}`}
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-md p-2 text-slate-500 hover:bg-muted"
      >
        <Bell size={18} />
        {unread.length > 0 && (
          <span className="absolute right-1 top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-40 mt-1 w-80 overflow-hidden rounded-lg border border-border bg-white shadow-lg">
            <p className="border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Notifications
            </p>
            {notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Nothing yet. @-mentions land here.
              </p>
            ) : (
              <ul className="max-h-80 overflow-y-auto">
                {notifications.slice(0, 12).map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!n.read_at) void markRead.mutateAsync(n.id);
                        setOpen(false);
                        navigate(n.source_type === 'message' ? '/app/messages' : '/app/feed');
                      }}
                      className={cn(
                        'block w-full px-4 py-2 text-left text-sm hover:bg-muted',
                        !n.read_at && 'bg-brand-50/50',
                      )}
                    >
                      <p className="font-medium text-foreground">
                        You were mentioned{n.source_type === 'message' ? ' in a message' : ' on the feed'}
                      </p>
                      {n.preview && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {n.preview}
                        </p>
                      )}
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {new Date(n.created_at).toLocaleString()}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function titleFor(path: string): string {
  if (path === '/app') return 'Today';
  if (path.startsWith('/app/tasks')) return 'Tasks';
  if (path.startsWith('/app/calendar')) return 'Calendar';
  if (path.startsWith('/app/feed')) return 'Family feed';
  if (path.startsWith('/app/messages')) return 'Messages';
  if (path.startsWith('/app/documents')) return 'Vault';
  if (path.startsWith('/app/sage')) return 'Sage';
  if (path.startsWith('/app/family')) return 'Family';
  if (path.startsWith('/app/care-recipient')) return 'Care recipient';
  if (path.startsWith('/app/medications')) return 'Medications';
  if (path.startsWith('/app/settings')) return 'Settings';
  return 'Familycare OS';
}
